import { Menu, ChevronDown, LogOut } from 'lucide-react'
import { useLocation } from 'react-router'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useIdentity } from '@/hooks/useIdentity'
import { getAvatarInitials, getDisplayName } from '@/lib/accessIdentity'
import { getPageTitle } from '@/lib/routes'
import { isTestbed, TestbedNotSignedInError } from '@/lib/testbed'
import { cn } from '@/lib/utils'

interface TopbarProps {
  onMenuClick: () => void
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const location = useLocation()
  const { data: identity, isLoading, error: identityError } = useIdentity()
  // On the testbed a failed identity call is either "nobody is signed in on the proxy" (a 401) or a
  // proxy problem; "Signed in" would be wrong for both, and the notice under the top bar says which.
  let displayName = getDisplayName(identity)
  if (isTestbed() && identityError) {
    displayName =
      identityError instanceof TestbedNotSignedInError ? 'Not signed in' : 'Identity unavailable'
  }
  const initials = getAvatarInitials(identity)
  const userEmail = identity?.email || null
  const pageTitle = getPageTitle(location.pathname)

  const handleLogout = () => {
    window.location.href = '/cdn-cgi/access/logout'
  }

  return (
    <header className="sticky top-0 z-50 flex h-16 items-center gap-4 border-b border-border bg-surface px-4 md:px-6">
      {/* Left side: Logo (desktop/tablet) or Hamburger (mobile) */}
      <div className="flex items-center gap-2">
        {/* Logo - visible on desktop/tablet, hidden on mobile */}
        {/* Hamburger - visible only on mobile */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="h-9 w-9 text-textMuted hover:text-text hover:bg-surface2 md:hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {/* Center/Left: Dynamic page title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-semibold text-text truncate sm:text-xl">{pageTitle}</h1>
      </div>

      {/* Right side: User menu */}
      <div className="flex items-center gap-3">
        {/* User menu */}
        {isLoading ? (
          <div className="h-10 w-32 animate-pulse rounded-full bg-surface2" />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 px-2 text-text hover:bg-surface2"
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-[rgba(255,255,255,0.08)] text-xs font-medium text-text',
                    'transition-colors hover:border-borderStrong'
                  )}
                >
                  {initials}
                </div>
                <span className="hidden sm:inline text-textMuted">{displayName}</span>
                <ChevronDown className="h-4 w-4 text-textMuted" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 border-border bg-surface shadow-lg backdrop-blur-xs"
            >
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none text-text">Signed in as</p>
                  {userEmail && <p className="text-xs leading-none text-textMuted">{userEmail}</p>}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-text hover:bg-surface2 focus:bg-surface2"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
